import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { brandImage } from "@/lib/brand";
import { Zap, Shield, Sparkles, ArrowRight } from "lucide-react";

export default function Landing() {
  const { branding } = useAuth();
  const { theme } = useTheme();
  const heroLogo = brandImage(branding, theme, "hero_logo");
  const footerLogo = brandImage(branding, theme, "footer_logo");
  return (
    <div className="stream-dark min-h-screen">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          {/* Desktop: logo left + buttons right */}
          <div className="hidden sm:flex items-center justify-between">
            <Logo size="lg" light />
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Link to="/login"><Button variant="ghost" className="text-white hover:bg-white/10" data-testid="nav-login">Prijava</Button></Link>
              <Link to="/register"><Button className="btn-yellow rounded-full px-6" data-testid="nav-register">Registracija</Button></Link>
            </div>
          </div>
          {/* Mobile: logo on top full width, buttons below */}
          <div className="sm:hidden flex flex-col items-center gap-3">
            <div className="flex items-center justify-between w-full">
              <Logo size="lg" light />
              <ThemeToggle />
            </div>
            <div className="flex items-center gap-2 w-full">
              <Link to="/login" className="flex-1"><Button variant="outline" className="w-full bg-transparent border-white/20 text-white hover:bg-white/10 rounded-full" data-testid="nav-login">Prijava</Button></Link>
              <Link to="/register" className="flex-1"><Button className="btn-yellow rounded-full w-full" data-testid="nav-register">Registracija</Button></Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden stream-grain">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#FFD60A] rounded-full blur-[160px] opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-24 md:py-32 relative">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6 flex justify-center order-1 lg:order-none">
              <div className="relative w-56 h-56 sm:w-80 sm:h-80 lg:w-96 lg:h-96">
                <div className="absolute inset-0 rounded-full bg-[#FFD60A] blur-[100px] sm:blur-[140px] opacity-40"></div>
                {heroLogo ? (
                  <img
                    src={heroLogo}
                    alt={branding.application_name}
                    className="relative w-full h-full object-contain drop-shadow-[0_0_80px_rgba(255,214,10,0.45)]"
                    data-testid="hero-logo-img"
                  />
                ) : (
                  <div className="relative w-full h-full rounded-full border-2 border-[#FFD60A]/40 flex items-center justify-center">
                    <div className="text-[8rem] sm:text-[10rem] font-heading font-black text-[#FFD60A] leading-none">S</div>
                  </div>
                )}
              </div>
            </div>
            <div className="lg:col-span-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 mb-5 sm:mb-8">
                <span className="w-2 h-2 rounded-full bg-[#FFD60A] animate-pulse"></span>
                <span className="text-[10px] sm:text-xs uppercase tracking-widest text-white/80">Payment Platforma</span>
              </div>
              <p className="text-xl sm:text-3xl font-heading font-black text-white leading-tight">{branding.slogan}</p>
              <p className="mt-3 sm:mt-4 text-base sm:text-lg text-white/60 max-w-xl mx-auto lg:mx-0">{branding.secondary_slogan}</p>
              <div className="mt-8 sm:mt-10 flex flex-wrap justify-center lg:justify-start gap-3">
                <Link to="/register">
                  <Button size="lg" className="btn-yellow rounded-full px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base" data-testid="hero-register">
                    Registriraj se <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="rounded-full px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base bg-transparent border-white/20 text-white hover:bg-white/10" data-testid="hero-login">
                    Prijavi se
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-24">
        <div className="max-w-2xl mb-10 sm:mb-16">
          <div className="text-xs uppercase tracking-widest text-[#FFD60A] mb-3">Zašto STREAM</div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl text-white leading-tight">Moderna platforma za naplatu</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
          {[
            { icon: Sparkles, title: "Inovativna rješenja", body: "Kreiramo moderne i funkcionalne aplikacije prilagođene vašim potrebama." },
            { icon: Zap, title: "Brzina i kvaliteta", body: "Koristimo moderne tehnologije kako bismo osigurali vrhunske performanse." },
            { icon: Shield, title: "Sigurnost na prvom mjestu", body: "Vaši podaci i korisnici su zaštićeni naprednim sigurnosnim standardima." },
          ].map((f, i) => (
            <div key={i} className="card-dark p-6 sm:p-8 group hover:border-[#FFD60A]/30 transition-colors">
              <div className="w-12 h-12 rounded-2xl bg-[#FFD60A]/10 border border-[#FFD60A]/20 flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-[#FFD60A] transition-colors">
                <f.icon className="text-[#FFD60A] group-hover:text-black" size={22} />
              </div>
              <h3 className="font-heading font-bold text-xl sm:text-2xl text-white mb-2 sm:mb-3">{f.title}</h3>
              <p className="text-sm sm:text-base text-white/60 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14 sm:pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-[#FFD60A] p-8 sm:p-12 md:p-16">
          <div className="max-w-2xl">
            <h2 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl text-black leading-tight">Spremni započeti?</h2>
            <p className="mt-3 sm:mt-4 text-black/70 text-base sm:text-lg">Kreirajte račun i primite prvi zahtjev za uplatu u nekoliko sekundi.</p>
            <div className="mt-6 sm:mt-8 flex flex-wrap gap-3">
              <Link to="/register"><Button size="lg" className="bg-black hover:bg-black/90 text-white rounded-full px-6 sm:px-8" style={{ color: "#ffffff" }} data-testid="cta-register">Registriraj se</Button></Link>
              <Link to="/login"><Button size="lg" variant="ghost" className="rounded-full px-6 sm:px-8 text-black hover:bg-black/10" data-testid="cta-login">Prijavi se</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 sm:py-10 text-center text-white/40 text-sm">
        <div className="max-w-3xl mx-auto px-4">
          {branding.footer_text ? (
            <div className="whitespace-pre-line text-white/60">{branding.footer_text}</div>
          ) : (
            <div>© {new Date().getFullYear()} {branding.application_name}. {branding.slogan}</div>
          )}
          {footerLogo && (
            <img
              src={footerLogo}
              alt="footer logo"
              className="h-16 sm:h-20 w-auto object-contain mx-auto mt-5 opacity-90"
              data-testid="footer-logo"
            />
          )}
          <div className="mt-5">
            <Link to="/privacy" className="text-white/50 hover:text-[#FFD60A] transition-colors" data-testid="footer-privacy-link">Politika privatnosti</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
